import { useCallback, useEffect, useRef, useState } from "react";

type UseDeferredExecutionParams = {
	/** Condition that must be true to execute (e.g., hasActiveToken) */
	condition: boolean;
	/** Optional precondition to allow arming/running (e.g., non-empty password) */
	canRun?: boolean;
	/** Optional timeout while waiting for condition before giving up */
	timeoutMs?: number;
	/** Optional callback when timeout is reached while pending */
	onTimeout?: () => void;
	/** Async action to perform once condition is true */
	execute: () => Promise<void>;
};

/**
 * Defers an async execution until a condition becomes true.
 * - call run() to start; if condition is false, it waits and exposes isBusy=true
 * - when condition flips to true, it executes and remains busy until completion
 * 
 * 
 * example usage:
 * 
 * useDeferredExecution({
			condition: hasActiveToken, // condition which will be checked to start the execution
			canRun: Boolean(formData.password), // precondition to allow arming/running
			execute: executePasswordLogin, // async action to perform once condition is true
		});

	this exposes the following properties:
	- run: function to start the execution (it will fail if the condition is not true)
	- cancel: function to cancel the execution
	- isBusy: boolean to check if the execution is pending or executing

	example usage:

	const { run, cancel, isBusy } = useDeferredExecution({
		condition: hasActiveToken,
		canRun: Boolean(formData.password),
		execute: executePasswordLogin,
	});

	then you can use the run function to start the execution:

	run();

	or the cancel function to cancel the execution:

	cancel();
 */
export function useDeferredExecution({
	condition,
	canRun = true,
	timeoutMs,
	onTimeout,
	execute,
}: UseDeferredExecutionParams) {
	const [pending, setPending] = useState(false);
	const [isExecuting, setIsExecuting] = useState(false);
	const executeRef = useRef(execute);
	const onTimeoutRef = useRef(onTimeout);
	const timeoutRef = useRef<number | null>(null);
	useEffect(() => {
		executeRef.current = execute;
	}, [execute]);
	useEffect(() => {
		onTimeoutRef.current = onTimeout;
	}, [onTimeout]);

	/**
	 * Runs the execution immediately.
	 */
	const runNow = useCallback(async () => {
		setIsExecuting(true);
		try {
			await executeRef.current();
		} finally {
			setIsExecuting(false);
		}
	}, []);

	/**
	 * Runs the execution if the condition is true.
	 */
	const run = useCallback(() => {
		if (!canRun) return;
		if (condition) {
			void runNow();
			return;
		}
		setPending(true);
	}, [canRun, condition, runNow]);

	useEffect(() => {
		if (pending && condition && !isExecuting) {
			setPending(false);
			void runNow();
		}
	}, [pending, condition, isExecuting, runNow]);

	useEffect(() => {
		if (!pending || !timeoutMs || timeoutMs <= 0) return;
		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = window.setTimeout(() => {
			setPending(false);
			onTimeoutRef.current?.();
		}, timeoutMs);
		return () => {
			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [pending, timeoutMs]);

	/**
	 * Cancels the execution.
	 */
	const cancel = useCallback(() => {
		setPending(false);
		setIsExecuting(false);
		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	/**
	 * Returns the run, cancel and isBusy functions.
	 */
	return {
		run,
		cancel,
		isBusy: pending || isExecuting,
	} as const;
}

export default useDeferredExecution;
